// Narrative + economics helpers for the Insights section and map popups.
//
// Crash-cost figures: FHWA "Crash Costs for Highway Safety Analysis" report
// (FHWA-SA-17-071, 2018) — COMPREHENSIVE societal cost per crash by KABCO
// maximum-injury severity, in 2016 dollars. Comprehensive cost includes both
// economic losses and quality-of-life valuation; it is the figure FHWA
// recommends for benefit-cost analysis of safety countermeasures.
import { NET_CONDITIONS } from '../SafetyOverview/rules';

export const KABCO_COSTS_2016USD = {
  K: 11_295_400, // Fatal
  A: 655_000,    // Suspected serious injury
  B: 198_500,    // Suspected minor injury
  C: 125_600,    // Possible injury
  O: 11_900,     // Property damage only
};
export const KABCO_SOURCE =
  'FHWA-SA-17-071 (2018), comprehensive crash costs, 2016 dollars';

/** KABCO class from the crash record: prefer MAX_INJR_SVRTY_CL text, fall back
 *  to the normalized severity class. */
export function kabcoOf(crash) {
  const t = (crash.maxInjury || '').toLowerCase();
  if (t.includes('fatal')) return 'K';
  if (t.includes('serious')) return 'A';
  if (t.includes('minor')) return 'B';
  if (t.includes('possible')) return 'C';
  if (t.includes('no injury') || t.includes('no apparent')) return 'O';
  return { fatal: 'K', injury: 'B', pdo: 'O', unknown: 'O' }[crash.severityClass] || 'O';
}

export const fmtUsd = (n) =>
  n >= 1_000_000 ? `$${(n / 1_000_000).toFixed(1)}M` : `$${Math.round(n / 1000)}K`;

const SEV_TEXT = {
  fatal: 'a fatal injury', injury: 'injury', pdo: 'property damage only', unknown: 'unknown severity',
};

/** "SR-2 EB · North Adams · MP 13.456" */
export function lrsLine(crash, road) {
  const dir = crash.direction === 'P' ? 'EB' : 'WB';
  const town = crash.city
    ? crash.city.replace(/\w\S*/g, (w) => w[0] + w.slice(1).toLowerCase())
    : null;
  return [road ? `${road.replace(/ (EB|WB|NB|SB)$/, '')} ${dir}` : dir, town,
    `MP ${crash.milepost?.toFixed(3)}`].filter(Boolean).join(' · ');
}

/** One-sentence crash description. */
export function crashSentence(crash) {
  const manner = (crash.manner || 'crash').toLowerCase();
  const what = manner === 'single vehicle crash' ? 'A single-vehicle crash'
    : manner === 'unknown' ? 'A crash' : `A ${manner} crash`;
  const parts = [`${what} occurred with ${SEV_TEXT[crash.severityClass] || 'unknown severity'}`];
  const surface = (crash.surface || '').toLowerCase();
  if (surface && surface !== 'dry') parts.push(`on a ${surface} surface`);
  const light = (crash.light || '').toLowerCase();
  if (light.includes('dark')) parts.push('in darkness');
  else if (light.includes('dawn') || light.includes('dusk')) parts.push(`at ${light}`);
  if (crash.isAnimal) parts.push('(animal strike)');
  const fh = (crash.firstHarmful || '').toLowerCase();
  if (crash.isEmbankment) parts.push(`— the vehicle ${fh.includes('overturn') || fh.includes('rollover') ? 'overturned' : 'hit the embankment'}`);
  return `${parts.join(' ')}.`;
}

/** Deficiency finding -> narrative sentence ("RDV <analysis> analysis found ..."). */
export function findingSentence(analysis, f, crash) {
  switch (analysis) {
    case 'SSD': {
      const sightLine = (f.eyeMp != null && f.targetMp != null)
        ? ` (observed from MP ${f.eyeMp.toFixed(3)} toward the target at MP ${f.targetMp.toFixed(3)} — the report row is keyed by the observer position)`
        : '';
      const base = `RDV SSD analysis found stopping sight distance good for ${f.availableFt} ft `
        + `where ${f.requiredFt} ft is required — the geometry supports ${f.safeSpeed} mph `
        + `while the regulatory speed is ${f.postedSpeed} mph${sightLine}.`;
      return f.animalCaveat
        ? `${base} For an animal strike, limited sight distance shortens the driver's `
          + `chance to detect and brake for wildlife — a possible, though not definitive, `
          + `case-dependent contributor.`
        : base;
    }
    case 'PSD': {
      // Safety Overview rules: a passing zone the MassDOT SOP forbids (near an intersection,
      // too short, below the minimum speed) is a PSD deficiency too
      const sop = (f.reasons || []).filter((r) => r !== 'unsafe');
      const SOP_TEXT = { intersection: 'lies within the SOP distance of an intersection', shortZone: 'is shorter than the SOP minimum',
        lowSpeed: 'is on a road below the SOP minimum speed',
        ...Object.fromEntries(NET_CONDITIONS.map(([k, , near]) => [k, `lies within the SOP distance of ${near}`])) };
      const sopText = sop.length ? `The marked passing zone here ${sop.map((r) => SOP_TEXT[r] || r).join(' and ')} (MassDOT SOP TS-2021-001). ` : '';
      if (f.availableFt == null) {
        return `${sopText}This ${(crash?.manner || 'opposite-direction').toLowerCase()} crash is the type a `
          + `failed or aborted passing manoeuvre produces, so the passing zone is a plausible contributor.`;
      }
      return sopText + `RDV PSD analysis found passing sight distance of ${f.availableFt} ft `
        + `where ${f.requiredFt} ft is required at ${f.postedSpeed} mph — `
        + `an overtaking vehicle here cannot see far enough to pass safely. `
        + `This ${(crash?.manner || 'opposite-direction').toLowerCase()} crash is the type a `
        + `failed or aborted passing manoeuvre produces, so the passing deficiency is a `
        + `plausible contributor.`;
    }
    case 'FSA':
      return `RDV FSA (Flat Spot Analysis) found the gradient here is ${Math.abs(f.gradientPct ?? f.crossSlopePct)}%`
        + (f.gradientPct != null && f.crossSlopePct != null
          ? ` (cross slope ${Math.abs(f.crossSlopePct)}%${f.longSlopePct != null ? `, long slope ${Math.abs(f.longSlopePct)}%` : ''})` : '')
        + ` while water drains only above ${f.thresholdPct}% — water or ice accumulates in this area, `
        + `and the surface was ${(crash?.surface || 'wet').toLowerCase()} at the time of the crash.`;
    case 'GLR': {
      const glrBase = `RDV GLR analysis found the crash's date, time and travel direction fall `
        + `inside this location's sun-glare window (~${f.hoursYr} hours of blinding `
        + `glare per year here) — sun glare is a possible contributing factor.`;
      return f.animalCaveat
        ? `${glrBase} With the sun in the driver's eyes, wildlife on the road is `
          + `detected later — glare may have contributed to this animal strike `
          + `(case-dependent, not definitive).`
        : glrBase;
    }
    case 'GRW':
      if (f.existingGuardrail) {
        return `RDV GRW analysis warrants a guardrail here (embankment `
          + `~${f.embankmentHeightFt} ft at slope ${f.slopeRatio || 'n/a'}) — a guardrail `
          + `is already in place and the vehicle struck it, likely preventing a more `
          + `severe outcome. The crash validates the warrant.`;
      }
      if (f.bridged && f.embankmentHeightFt == null) {
        return `RDV GRW analysis warrants a guardrail on both sides of this spot; the gap between them is short enough `
          + `to be treated as warranted too (Settings → Filters).`;
      }
      return `RDV GRW analysis found this spot warrants a guardrail `
        + `(embankment ~${f.embankmentHeightFt} ft at slope ${f.slopeRatio || 'n/a'})`
        + `${crash?.isEmbankment ? ' — the vehicle went over the unprotected embankment' : ''}.`;
    case 'HCA':
      return `RDV HCA analysis rates this curve at a ${f.scorePct}% safety score — `
        + `safe for ${f.requiredSpeed} mph while posted at ${f.postedSpeed} mph `
        + `(curve #${f.curveNumber}, radius ${f.radiusFt} ft).`;
    case 'ISD':
      return `RDV ISD analysis places this crash ${f.crashDistanceFt} ft from intersection `
        + `${f.intersectionId || ''} — inside its required ${f.requiredFt} ft sight distance.`;
    default:
      return '';
  }
}

/** Findings visible under the current analysis filter ('ALL' -> every finding). */
export function visibleFindings(crash, analysis) {
  const f = crash.findings || {};
  if (analysis === 'ALL') return Object.entries(f);
  return f[analysis] ? [[analysis, f[analysis]]] : [];
}

/** Economics summary over correlated crashes (>=1 visible finding). Animal
 *  strikes count like any other crash (operator decision 2026-09-09): a
 *  strike at an SSD / PSD / GLR deficiency is a candidate for prevention too.
 *  The "Exclude animal strikes" filter removes them upstream when wanted.
 *  costTable comes from the crashes payload (per-state registry choice:
 *  MassDOT's 2024 table for Massachusetts, FHWA for other states). */
export function computeImpactSummary(correlated, costTable) {
  const costs = costTable?.values || KABCO_COSTS_2016USD;
  const source = costTable?.source || KABCO_SOURCE;
  let cost = 0;
  const byKabco = {};
  let lives = 0;
  let mitigated = 0;
  let animal = 0;
  for (const c of correlated) {
    const k = kabcoOf(c);
    byKabco[k] = (byKabco[k] || 0) + 1;
    cost += costs[k] || 0;
    if (k === 'K') lives += (c.numFatal || 1);
    if (c.findings?.GRW?.existingGuardrail) mitigated += 1;
    if (c.isAnimal) animal += 1;
  }
  return { n: correlated.length, byKabco, cost, lives, mitigated, animal, source };
}
